/**
 * ChronicleSubjectsEditor — DEV-1396.
 *
 * The descriptive tray "Subjects" slot value: the chronicle's author-declared
 * subject tags (platform_skills slugs) as chips, plus an author-only edit
 * affordance that opens a SkillPicker popover to add/remove subjects.
 *
 * Subjects route content to matching reviewers (direct-matched against reviewer
 * skill endorsements for Gate-2 routing) — at least one is required before
 * submit-for-expert-review (the server 400s without one; the UX gates earlier).
 *
 * Picker source reuses the Stage-2 (DEV-1378) pattern: `fetchPlatformSkills`
 * with `canonicalOnly` + debounced search autocomplete, the same source the
 * Profile Capability Registry uses. Read-only chips for non-authors; the whole
 * value collapses to an em-dash for non-authors when there are no subjects.
 *
 * Kept compact for the tray card — the picker is a small opaque popover anchored
 * to the edit button, never crammed inline into the card.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { setChronicleSubjects, type ChronicleSubjectResponse } from '../../api/chronicles';
import SkillPicker, { type SkillPickerSelection } from '../SkillPicker/SkillPicker';

interface Props {
  chronicleGuid: string;
  subjects: ChronicleSubjectResponse[];
  /** Author/sysadmin — gates the edit affordance and the picker. */
  canEdit: boolean;
  /** Called after a successful save so the parent can reload the chronicle. */
  onChanged: () => void;
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-primary)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 999,
  padding: '2px 9px',
  whiteSpace: 'nowrap',
};

export default function ChronicleSubjectsEditor({ chronicleGuid, subjects, canEdit, onChanged }: Props) {
  const { t } = useAppTranslation('pages');

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Local working copy of the subject slugs while the picker is open.
  const [working, setWorking] = useState<ChronicleSubjectResponse[]>(subjects);

  const wrapRef = useRef<HTMLDivElement>(null);

  // Re-seed the working copy whenever the source subjects change or the popover opens.
  useEffect(() => {
    if (!open) setWorking(subjects);
  }, [subjects, open]);

  // Close the popover on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const persist = useCallback((next: ChronicleSubjectResponse[]) => {
    setSaving(true);
    setChronicleSubjects(chronicleGuid, next.map((s) => s.skillSlug))
      .then((res) => {
        setWorking(res.subjects);
        onChanged();
      })
      .catch(() => {
        // Revert the optimistic edit on failure.
        setWorking(subjects);
      })
      .finally(() => setSaving(false));
  }, [chronicleGuid, onChanged, subjects]);

  const handleAdd = useCallback((skill: SkillPickerSelection) => {
    if (working.some((s) => s.skillSlug.toLowerCase() === skill.skillSlug.toLowerCase())) return;
    const next = [...working, { skillSlug: skill.skillSlug, label: skill.label }];
    setWorking(next);
    persist(next);
  }, [working, persist]);

  const handleRemove = useCallback((slug: string) => {
    const next = working.filter((s) => s.skillSlug.toLowerCase() !== slug.toLowerCase());
    setWorking(next);
    persist(next);
  }, [working, persist]);

  const shown = open ? working : subjects;

  // Non-author with no subjects → em-dash (the slot's empty value).
  if (!canEdit && shown.length === 0) {
    return <>{'—'}</>;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {shown.length === 0 ? (
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {t('chronicleView.subjects.empty')}
        </span>
      ) : (
        shown.map((s) => (
          <span key={s.skillSlug} style={chipStyle}>
            {s.label}
            {canEdit && open && (
              <button
                type="button"
                onClick={() => handleRemove(s.skillSlug)}
                disabled={saving}
                aria-label={t('chronicleView.subjects.remove', { subject: s.label })}
                style={{
                  background: 'transparent', border: 'none', color: 'var(--text-tertiary)',
                  cursor: saving ? 'default' : 'pointer', fontSize: 13, lineHeight: 1, padding: 0,
                  marginInlineStart: 2,
                }}
              >
                ×
              </button>
            )}
          </span>
        ))
      )}

      {/* Edit affordance — author only. */}
      {canEdit && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            fontSize: 12, fontWeight: 600, color: 'var(--accent-primary)',
            background: 'transparent', border: '1px dashed var(--border-default)',
            borderRadius: 999, padding: '2px 9px', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {open ? t('chronicleView.subjects.done') : t('chronicleView.subjects.edit')}
        </button>
      )}

      {/* Picker popover — SkillPicker renders its own opaque panel; this wrapper
          just anchors + sizes it below the chip row. */}
      {canEdit && open && (
        <div
          style={{
            position: 'absolute', top: '100%', insetInlineStart: 0, marginBlockStart: 6, zIndex: 40,
            width: 300,
          }}
        >
          <SkillPicker
            selected={working.map((s) => ({ skillSlug: s.skillSlug, label: s.label }))}
            onAdd={handleAdd}
            onRemove={handleRemove}
            disabled={saving}
          />
        </div>
      )}
    </div>
  );
}
